import { AppUserValue } from 'lib/actions/apps/app-users'
import { TaskValue } from 'lib/actions/task'
import { expandValue, expandValueList } from 'lib/rpcts'
import { AccessReview } from 'pbts/c1/api/accessreview/v1/accessreview'
import { AccessConflictOutcome, AccountOutcome, GlobalOutcome, ManagerOutcome, UserPropOutcome } from 'pbts/c1/api/analysis/v1/analysis'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppEntitlement } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppUser } from 'pbts/c1/api/app/v1/app_user'
import {
    BulkActionProgress,
    TaskActionsServiceApproveResponse,
    TaskActionsServiceApproveWithStepUpResponse,
    TaskActionsServiceCloseResponse,
    TaskActionsServiceCommentResponse,
    TaskActionsServiceDenyResponse,
    TaskActionsServiceGetSingleBulkActionProgressByIdResponse,
    TaskActionsServiceProcessNowResponse,
    TaskBulkActionRef,
    TaskServiceActionResponse,
} from 'pbts/c1/api/task/v1/actions'
import { TaskActionsServiceClient } from 'pbts/c1/api/task/v1/actions.client'
import { TaskAuditView, TaskAuditViewRef } from 'pbts/c1/api/task/v1/audit'
import { TaskAuditClient } from 'pbts/c1/api/task/v1/audit.client'
import { TaskAuditSearchServiceSearchResponse } from 'pbts/c1/api/task/v1/audit_search'
import { TaskAuditSearchServiceClient } from 'pbts/c1/api/task/v1/audit_search.client'
import { TaskBinding, TaskBindingView } from 'pbts/c1/api/task/v1/binding'
import { TaskBindingServiceClient } from 'pbts/c1/api/task/v1/binding.client'
import { Insight } from 'pbts/c1/api/task/v1/insights'
import {
    SearchWaitingOnResponse,
    TaskSearchResponse,
    TaskSearchServiceSearchTasksByGrantsResponse,
} from 'pbts/c1/api/task/v1/search'
import { TaskSearchServiceClient } from 'pbts/c1/api/task/v1/search.client'
import { GrantOrRevokeTaskRef, ResolvedFormData, Task, TaskCountRefreshRef, TaskRef, TaskView } from 'pbts/c1/api/task/v1/task'
import { TaskServiceClient } from 'pbts/c1/api/task/v1/task.client'
import { User } from 'pbts/c1/api/user/v1/user'
import { Any } from 'pbts/google/protobuf/any'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { Code as pbCode } from 'pbts/google/rpc/code'
import { Status as pbStatus } from 'pbts/google/rpc/status'
import { createMutationApi } from 'redux_toolkit/lib/createMutationApi'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'
import { createUnaryApi } from 'redux_toolkit/lib/createUnaryApi'

const tag = TaskRef.typeName

export interface TaskBindingValue extends TaskBinding {
    id: string
    taskValue?: TaskValue
    referencedTaskValue?: TaskValue
}

const expandTaskBindingView = (msg: TaskBindingView | undefined, values: Any[]): TaskBindingValue => ({
    ...TaskBinding.create(msg?.item ?? {}),
    taskValue: expandTaskView(msg?.item?.task, values),
    referencedTaskValue: expandTaskView(msg?.item?.referencedTask, values),
})

const expandTaskAuditView = (msg: TaskAuditView | undefined, _: Any[]): TaskAuditView => ({
    ...TaskAuditView.create(msg ?? {}),
})

const transformAuditArrayResponse = (resp): Array<TaskAuditView> => {
    return resp.list.map((v): TaskAuditView => expandTaskAuditView(v, v.expanded))
}

export const expandInsights = (insights?: Insight[]): Insight[] => {
    if (!insights) {
        return [] as Insight[]
    }
    const expandedInsights = insights.map((insight) => {
        if (insight.kind.oneofKind === 'analysis') {
            const outcome = insight.kind.analysis?.outcome
            let expandedOutcome
            if (outcome?.outcome) {
                switch (Any.typeUrlToName(outcome?.outcome?.typeUrl || '')) {
                    case GlobalOutcome.typeName:
                        expandedOutcome = Any.unpack<GlobalOutcome>(outcome.outcome, GlobalOutcome)
                        break
                    case AccountOutcome.typeName:
                        expandedOutcome = Any.unpack<AccountOutcome>(outcome.outcome, AccountOutcome)
                        break
                    case UserPropOutcome.typeName:
                        expandedOutcome = Any.unpack<UserPropOutcome>(outcome.outcome, UserPropOutcome)
                        break
                    case ManagerOutcome.typeName:
                        expandedOutcome = Any.unpack<ManagerOutcome>(outcome.outcome, ManagerOutcome)
                        break
                    case AccessConflictOutcome.typeName:
                        expandedOutcome = Any.unpack<AccessConflictOutcome>(outcome.outcome, AccessConflictOutcome)
                        break
                    default:
                        expandedOutcome = outcome.outcome
                        break
                }
                insight.kind.analysis = {
                    ...insight.kind.analysis,
                    outcome: {
                        ...outcome,
                        outcome: {
                            ...expandedOutcome,
                            typeUrl: outcome.outcome.typeUrl,
                        },
                    },
                }
                return insight
            }
        }
        return insight
    })
    return expandedInsights
}

// TODO this is the expander
export const expandTaskAppUser = (msg: TaskView | undefined, values: Any[]): AppUserValue | undefined => {
    const appUser = expandValue<AppUser>(msg?.appUserPath || '', AppUser, values)
    if (!appUser) {
        return undefined
    }

    const appUserValue: AppUserValue = AppUser.create(appUser)
    appUserValue.lastUsedAt = expandValue<Timestamp>(msg?.appUserLastUsagePath || '', Timestamp, values)
    return appUserValue
}

const expandTaskView = (msg: TaskView | undefined, values: Any[]): TaskValue => {
    let app = expandValue<App>(msg?.appPath || '', App, values)
    if (app === undefined) {
        const appErr = expandValue<pbStatus>(msg?.appPath || '', pbStatus, values)
        if (appErr?.code === pbCode.NOT_FOUND) {
            app = App.create({
                displayName: 'Deleted App',
            })
        }
    }

    const expandedInsights = expandInsights(expandValueList<Insight>(msg?.insightsPath || '', Insight, values))

    return {
        ...Task.create(msg?.task ?? {}),
        accessReview: expandValue<AccessReview>(msg?.accessReviewPath || '', AccessReview, values),
        user: expandValue<User>(msg?.userPath || '', User, values),
        appUser: expandTaskAppUser(msg, values),
        createdByUser: expandValue<User>(msg?.createdByUserPath || '', User, values),
        appEntitlements: expandValueList<AppEntitlement>(msg?.entitlementsPath || '', AppEntitlement, values),
        stepApprovers: expandValueList<User>(msg?.stepApproversPath || '', User, values),
        app,
        status: undefined,
        identityUser: expandValue<User>(msg?.identityUserPath || '', User, values),
        insights: expandedInsights,
    }
}

const transformTaskSearchResponse = (resp: TaskSearchResponse): Array<TaskValue> => {
    return resp.list.map((v): TaskValue => expandTaskView(v, resp.expanded))
}

const transformTaskSearchTasksByGrantsResponse = (resp: TaskSearchServiceSearchTasksByGrantsResponse): Array<TaskValue> => {
    return resp.list.map((v): TaskValue => Task.create(v.task ?? {}))
}

const transformTaskSearchWaitingOnResponse = (resp: SearchWaitingOnResponse): Array<TaskValue> => {
    return resp.waitingOnTasks.map((v): TaskValue => expandTaskView(v, resp.expanded))
}

// ------ Lists ------
export const TaskAuditApi = createPagedListApi(TaskAuditClient, 'list', transformAuditArrayResponse, TaskAuditViewRef.typeName)
export const tasksSearchApi = createPagedListApi(TaskSearchServiceClient, 'search', transformTaskSearchResponse, tag)
export const tasksSearchByGrantsApi = createPagedListApi(
    TaskSearchServiceClient,
    'searchTasksByGrants',
    transformTaskSearchTasksByGrantsResponse,
    tag,
)
export const tasksSearchWaitingOnApi = createUnaryApi(TaskSearchServiceClient, 'searchWaitingOn', transformTaskSearchWaitingOnResponse, tag)

// ------ Objects ------
export const getTaskApi = createUnaryApi(TaskServiceClient, 'get', (t) => expandTaskView(t.taskView, t.expanded), tag)
export const getResolvedFormDataApi = createUnaryApi(
    TaskServiceClient,
    'getByTaskId',
    (resp) => resp.resolvedFormData || ResolvedFormData.create({}),
    'ResolvedFormData',
)
export const getTaskSearchCountApi = createUnaryApi(TaskSearchServiceClient, 'searchCount', undefined, TaskCountRefreshRef.typeName)
export const getTaskSearchCountByTypeApi = createUnaryApi(TaskSearchServiceClient, 'searchCountByType', undefined, TaskCountRefreshRef.typeName)
export const countCompletedTicketsWithinMonthClient = createUnaryApi(TaskSearchServiceClient, 'countCompletedTicketsWithinMonth')
export const getBulkActionProgressByIdApi = createUnaryApi(
    TaskActionsServiceClient,
    'getSingleBulkActionProgressById',
    (resp: TaskActionsServiceGetSingleBulkActionProgressByIdResponse) => BulkActionProgress.create(resp.bulkActionProgress ?? {}),
    TaskBulkActionRef.typeName,
)
export const listBulkActionInProgressApi = createUnaryApi(TaskActionsServiceClient, 'listBulkActionInProgress')
export const countAuditEventTypeErrorApi = createUnaryApi(TaskSearchServiceClient, 'countAuditEventTypeError')
export const getAllowedBulkActionsApi = createUnaryApi(TaskActionsServiceClient, 'getAllowedBulkActions')
export const countOpenExtensionTickets = createUnaryApi(TaskSearchServiceClient, 'countOpenExtensionTickets')
export const listOpenExtensionTickets = createPagedListApi(TaskSearchServiceClient, 'listOpenExtensionTickets')

// ------ Mutations ------
export const createActionTaskApi = createMutationApi(
    TaskServiceClient,
    'createActionTask',
    (resp) => expandTaskView(resp.taskView, resp.expanded),
    tag,
)

export const approveTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'approve',
    (resp: TaskActionsServiceApproveResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)

export const approveWithStepUpTask = createUnaryApi(
    TaskActionsServiceClient,
    'approveWithStepUp',
    (resp: TaskActionsServiceApproveWithStepUpResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
            stepUpRedirectUrl: resp.redirectUrl,
        }
    },
    tag,
)

export const approveWithStepUpTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'approveWithStepUp',
    (resp: TaskActionsServiceApproveWithStepUpResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
            stepUpRedirectUrl: resp.redirectUrl,
        }
    },
    tag,
)

export const denyTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'deny',
    (resp: TaskActionsServiceDenyResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)
export const markProvisionCompleteApi = createMutationApi(
    TaskActionsServiceClient,
    'markProvisionComplete',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)
export const markProvisionErroredApi = createMutationApi(
    TaskActionsServiceClient,
    'markProvisionErrored',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)
export const commentOnTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'comment',
    (resp: TaskActionsServiceCommentResponse): TaskValue => {
        return expandTaskView(resp.taskView, resp.expanded)
    },
    tag,
)
export const reassignTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'reassign',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)
export const cancelTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'close$',
    (resp: TaskActionsServiceCloseResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.taskActionId,
        }
    },
    tag,
)
export const restartTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'restart',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)

export const hardResetTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'hardReset',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)

export const processNowTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'processNow',
    (resp: TaskActionsServiceProcessNowResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
        }
    },
    tag,
)

export const escalateTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'escalateToEmergencyAccess',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)

export const skipStepTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'skipStep',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)

export const updateRequestDataTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'updateRequestData',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)

export const updateGrantDurationTaskApi = createMutationApi(
    TaskActionsServiceClient,
    'updateGrantDuration',
    (resp: TaskServiceActionResponse): TaskValue => {
        return {
            ...expandTaskView(resp.taskView, resp.expanded),
            ticketActionId: resp.ticketActionId,
        }
    },
    tag,
)

export const bulkActionApi = createMutationApi(TaskActionsServiceClient, 'bulkRequest')

// TODO Santhosh this returns only a taskId. We should invalidate the task somehow.
export const deleteTaskApi = createMutationApi(TaskServiceClient, 'delete')

const transformTaskBindingValue = (resp): Array<TaskBindingValue> => {
    return resp.list.map((v): TaskBindingValue => {
        return expandTaskBindingView(v, resp.expanded)
    })
}

export const listTaskBindingsApi = createPagedListApi(TaskBindingServiceClient, 'list', transformTaskBindingValue, tag)
export const listRelatedTasks = createPagedListApi(TaskBindingServiceClient, 'listRelatedTasks', transformTaskSearchResponse, tag)
export const countRelatedTasksApi = createUnaryApi(TaskBindingServiceClient, 'countRelatedTasks', undefined, TaskCountRefreshRef.typeName)

export const createGrantTaskApi = createMutationApi(
    TaskServiceClient,
    'createGrantTask',
    (resp): TaskValue => expandTaskView(resp.taskView, resp.expanded),
)

export const createManyGrantTasksApi = createMutationApi(
    TaskServiceClient,
    'createManyGrantTasks',
    transformTaskSearchResponse,
)

export const createOffboardingTaskApi = createMutationApi(
    TaskServiceClient,
    'createOffboardingTask',
    (resp): TaskValue => expandTaskView(resp.taskView, resp.expanded),
)

export const getProgressApi = createUnaryApi(
    TaskSearchServiceClient,
    'getProgress',
    undefined,
    TaskCountRefreshRef.typeName,
)

export const rollupEntitlementsWithTasksApi = createPagedListApi(
    TaskSearchServiceClient,
    'rollupEntitlementsWithTasks',
    undefined,
    GrantOrRevokeTaskRef.typeName,
)

export type TaskAuditWithTaskValue = {
    audit: TaskAuditView
    task: TaskValue
}

export const taskAuditSearchApi = createPagedListApi(
    TaskAuditSearchServiceClient,
    'search',
    (resp: TaskAuditSearchServiceSearchResponse): Array<TaskAuditWithTaskValue> => (
        resp.list.map((v): TaskAuditWithTaskValue => ({
            audit: v.audit ?? TaskAuditView.create({}),
            task: expandTaskView(v.task, []),
        }))
    ),
)

export const searchCertifyTicketsByAppViewApi = createPagedListApi(
    TaskSearchServiceClient,
    'searchCertifyTicketsByAppView',
    transformTaskSearchResponse,
    tag,
)

export const searchCertifyTicketsByUserViewApi = createPagedListApi(
    TaskSearchServiceClient,
    'searchCertifyTicketsByUserView',
    transformTaskSearchResponse,
    tag,
)
